/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: testimonials.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@nextui-org/react';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { MdOutlineError } from 'react-icons/md';
import { QueryDocumentSnapshot, QuerySnapshot, collection, getDocs, query } from 'firebase/firestore';

import BasicComponents from '@/shared/basic.components';
import Section from '@/shared/section';
import FirebaseService from '@/services';
import Converters from '@/services/converters';
/**************************************************************************************************************************
 TYPES
***************************************************************************************************************************/
enum TestimonialState {
  LOADING = 1,
  SUCCESS = 2,
  FAILED = 3
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Testimonials: FunctionComponent = () => {
  const { t } = useTranslation('home-page');

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  const [status, setStatus] = useState<TestimonialState>(TestimonialState.LOADING);

  const getNext = () =>
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);

  const getBack = () =>
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);

  useEffect(() => {
    getDocs(
      query(
        collection(FirebaseService.StoreDB, 'testimonials').withConverter<Testimonial>(
          Converters.Testimonial
        )
      )
    )
      .then((snapshot: QuerySnapshot<Testimonial>) => {
        setTestimonials(
          snapshot.docs.map((documentSnapshot: QueryDocumentSnapshot<Testimonial>) =>
            documentSnapshot.data()
          )
        );
        setTimeout(() => setStatus(TestimonialState.SUCCESS), 2000);
      })
      .catch((err) => {
        console.log(err);
        setTimeout(() => setStatus(TestimonialState.FAILED), 2000);
      });
  }, []);

  return (
    <Section
      id="testimonials"
      className="relative bg-black"
    >
      <div className="absolute top-0 left-0 w-full h-full bg-black/70"></div>
      <div className="col-span-1 flex flex-col items-start justify-start gap-10">
        <BasicComponents.SectionHeader
          title={t('testimonials.title')}
          subtitle={t('testimonials.subtitle')}
        />
        {status === TestimonialState.FAILED ? (
          <div className="w-full bg-red-500 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
            <div className="w-[25px] h-[25px] flex items-center justify-center">
              <MdOutlineError className="text-xl text-white" />
            </div>
            <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
              {t('testimonials.error')}
            </p>
          </div>
        ) : status === TestimonialState.SUCCESS && testimonials.length > 0 ? (
          <motion.div
            className="w-full min-h-[225px] bg-theme-900 relative flex items-center justify-center gap-5 rounded-[200px] py-5 px-5"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                x: 100,
                opacity: 0
              },
              entry: {
                x: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5
            }}
          >
            <button
              className="group w-[50px] h-[50px] bg-black flex flex-col items-center justify-center rounded-full shadow-md drop-shadow-sm"
              onClick={getBack}
            >
              <FiChevronLeft className="text-3xl text-white group-hover:text-white transition-all duration-350" />
            </button>
            <div className="w-[calc(100%-100px)] h-full bg-white flex flex-col items-start justify-start rounded-[200px] gap-5 py-10 px-[100px]">
              <p className="text-truncate lg:w-full text-md lg:text-lg text-gray-500 font-roboto font-medium italic">
                {testimonials[currentIndex].comments}
              </p>
              <div className="w-full border-t-[2px] border-gray-500/30"></div>
              <h3 className="text-sm lg:text-sm text-black font-montserrat font-bold">
                {testimonials[currentIndex].name} |{' '}
                <span className="text-sm text-black font-electrolize font-bold">
                  {testimonials[currentIndex].role} - {testimonials[currentIndex].org}
                </span>
              </h3>
            </div>
            <button
              className="group w-[50px] h-[50px] bg-black flex flex-col items-center justify-center rounded-full shadow-md drop-shadow-sm"
              onClick={getNext}
            >
              <FiChevronRight className="text-3xl text-white group-hover:text-white transition-all duration-350" />
            </button>
          </motion.div>
        ) : status === TestimonialState.LOADING ? (
          <div className="w-full bg-themeBlue-900 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
            <div className="w-[25px] h-[25px] flex items-center justify-center">
              <Spinner size="sm" color="white" />
            </div>
            <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
              {t('testimonials.loading')}
            </p>
          </div>
        ) : (
          <div className="w-full bg-themePurple-900 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
            <div className="w-[25px] h-[25px] flex items-center justify-center">
              <MdOutlineError className="text-xl text-white" />
            </div>
            <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
              {t('testimonials.empty')}
            </p>
          </div>
        )}
      </div>
    </Section>
  );
};


export default Testimonials;